import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { getMeetingsByUser } from '../../services/meetingService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MeetingCard from './components/MeetingCard';
import StatisticsOverview from './components/StatisticsOverview';
import QuickActions from './components/QuickActions';
import { trackButtonClick, trackEngagementTime } from '../../utils/analytics';

const HomeDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [pageLoadTime, setPageLoadTime] = useState(null);

  useEffect(() => {
    setPageLoadTime(Date.now());
    
    return () => {
      // Track engagement time when leaving page
      if (pageLoadTime) {
        const timeSpent = Math.floor((Date.now() - pageLoadTime) / 1000);
        trackEngagementTime('Home Dashboard', timeSpent);
      }
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/signin');
      return;
    }

    loadMeetings();
  }, [user, authLoading, navigate]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getMeetingsByUser(user);
      setMeetings(data || []);
    } catch (err) {
      console.error('Load meetings error:', err);
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const categorizeMeetings = () => {
    const now = new Date();
    const upcoming = [];
    const active = [];
    const completed = [];

    meetings?.forEach(meeting => {
      const meetingDateTime = new Date(`${meeting?.meetingDate} ${meeting?.meetingTime}`);
      const timeDiff = meetingDateTime - now;
      const hoursDiff = timeDiff / (1000 * 60 * 60);

      if (meeting?.status === 'confirmed' && hoursDiff >= -2 && hoursDiff <= 2) {
        active?.push(meeting);
      } else if (meeting?.status === 'cancelled' || hoursDiff < -2) {
        completed?.push(meeting);
      } else {
        upcoming?.push(meeting);
      }
    });

    return { upcoming, active, completed };
  };

  const { upcoming, active, completed } = categorizeMeetings();

  const getFilteredMeetings = () => {
    switch (activeTab) {
      case 'upcoming':
        return upcoming;
      case 'active':
        return active;
      case 'completed':
        return completed;
      default:
        return [];
    }
  };

  const filteredMeetings = getFilteredMeetings();

  const handleCreateMeeting = () => {
    trackButtonClick('create_meeting', 'Create Meeting');
    navigate('/location-input');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackButtonClick(`tab_${tab}`, `${tab} Tab`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Home Dashboard - Mosey</title>
        <meta name="description" content="Manage all your meetings with status tracking and quick actions" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-6">
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
              Welcome back, {user?.email?.split('@')?.[0] || 'User'}
            </h1>
            <p className="text-muted-foreground">
              Manage your meetings and coordinate with participants
            </p>
          </div>

          {/* Primary CTA - Start a Mosey meetup */}
          <div className="mb-8">
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={handleCreateMeeting}
              fullWidth
              className="h-16 text-lg font-semibold shadow-lg hover:shadow-xl"
            >
              Start a Mosey meetup
            </Button>
          </div>

          {/* Statistics Overview */}
          <StatisticsOverview 
            upcomingCount={upcoming?.length || 0}
            activeCount={active?.length || 0}
            completedCount={completed?.length || 0}
            totalMeetings={meetings?.length || 0}
          />

          {/* Quick Actions */}
          <QuickActions onCreateMeeting={handleCreateMeeting} />

          {/* Meeting Categories */}
          <div className="bg-card rounded-lg shadow-md border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-foreground">
                Your Meetings
              </h2>
              <div className="flex gap-2">
                <Button
                  variant={activeTab === 'upcoming' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('upcoming')}
                >
                  Upcoming ({upcoming?.length || 0})
                </Button>
                <Button
                  variant={activeTab === 'active' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('active')}
                >
                  Active ({active?.length || 0})
                </Button>
                <Button
                  variant={activeTab === 'completed' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('completed')}
                >
                  Completed ({completed?.length || 0})
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-error">{error}</p>
                </div>
              </div>
            )}

            {filteredMeetings?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  No {activeTab} meetings
                </h3>
                <p className="text-muted-foreground mb-6">
                  {activeTab === 'upcoming' && 'Create a new meeting to get started'}
                  {activeTab === 'active' && 'No meetings are currently active'}
                  {activeTab === 'completed' && 'No completed meetings yet'}
                </p>
                {activeTab === 'upcoming' && (
                  <Button
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleCreateMeeting}
                  >
                    Create Meeting
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMeetings?.map(meeting => (
                  <MeetingCard
                    key={meeting?.id}
                    meeting={meeting}
                    category={activeTab}
                    onViewDetails={(id) => {
                      if (activeTab === 'active') {
                        navigate(`/active-meeting?meetingId=${id}`);
                      } else {
                        navigate(`/final-meeting-details?meetingId=${id}`);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Floating Action Button */}
          <button
            onClick={handleCreateMeeting}
            className="fixed bottom-8 right-8 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-smooth hover-lift active-press focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 flex items-center justify-center"
            aria-label="Create new meeting"
          >
            <Icon name="Plus" size={24} />
          </button>
        </main>
      </div>
    </>
  );
};

export default HomeDashboard;